import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Locale = 'ar' | 'en';
type Direction = 'rtl' | 'ltr';

interface AppState {
  /** Current locale (ar or en) */
  locale: Locale;
  /** Current text direction (rtl or ltr) */
  direction: Direction;
  /** Whether the sidebar is open (for mobile/tablet) */
  sidebarOpen: boolean;
}

interface AppActions {
  /** Set locale and update direction accordingly */
  setLocale: (locale: Locale) => void;
  /** Toggle between Arabic and English */
  toggleLocale: () => void;
  /** Toggle sidebar open/closed state */
  toggleSidebar: () => void;
  /** Set sidebar open state explicitly */
  setSidebarOpen: (open: boolean) => void;
}

type AppStore = AppState & AppActions;

/**
 * Global application store using Zustand
 * Persists locale preference to localStorage
 */
export const useAppStore = create<AppStore>()(
  persist(
    (set) => ({
      // Initial state - Arabic RTL by default
      locale: 'ar',
      direction: 'rtl',
      sidebarOpen: false,

      // Actions
      setLocale: (locale) =>
        set({
          locale,
          direction: locale === 'ar' ? 'rtl' : 'ltr',
        }),

      toggleLocale: () =>
        set((state) => ({
          locale: state.locale === 'ar' ? 'en' : 'ar',
          direction: state.locale === 'ar' ? 'ltr' : 'rtl',
        })),

      toggleSidebar: () =>
        set((state) => ({
          sidebarOpen: !state.sidebarOpen,
        })),

      setSidebarOpen: (open) =>
        set({
          sidebarOpen: open,
        }),
    }),
    {
      name: 'nour-arabia-app-storage',
      partialize: (state) => ({
        locale: state.locale,
        direction: state.direction,
      }),
    }
  )
);
